import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@mui/styles";
import {
  Card,
  CardActionArea,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";

import { getToolImages } from "../../../services/ToolService";
import { convertDate, formatPrice } from "../../../utils/function";

const defaultImage = "/assets/images/image-upload-placeholder.png";

const useStyles = makeStyles({
  root: {},
});

const ToolInventoryDetail = ({ data }) => {
  const classes = useStyles();
  const defaultImageData = useMemo(
    () => ({
      IMAGE_FILE_STREAM: null,
      IMAGE_NAME: null,
      STREAM_IDS: null,
      IMAGE: defaultImage,
    }),
    []
  );
  const [images, setImages] = useState([defaultImageData]);
  const [loading, setLoading] = useState(false);

  const retrieveToolImages = useCallback(
    async (toolId) => {
      try {
        setLoading(true);
        const res = await getToolImages(toolId);
        setLoading(false);

        const newImages = res.map((item) => ({
          ...item,
          IMAGE: item.IMAGE_FILE_STREAM
            ? `data:image/jpeg;base64, ${item.IMAGE_FILE_STREAM}`
            : defaultImage,
        })) || [defaultImageData];

        setImages(newImages);
      } catch (e) {
        console.error(e);
        toast.error("Unexpected error occurs while loading tool images!");
      }
    },
    [defaultImageData]
  );

  useEffect(() => {
    let isMounted = true;

    if (!data) return;

    retrieveToolImages(data.TOOL_ID).then(() => {
      if (!isMounted) return;
    });

    return () => {
      isMounted = false;
    };
  }, [data]);

  return (
    <Grid container alignItems="center">
      <Grid item xs={6}>
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  Purchase Date:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.PURCHASE_DATE
                    ? convertDate(data?.PURCHASE_DATE)
                    : "Undefined"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  Price:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.PRICE ? formatPrice(data?.PRICE) : "Undefined"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  Date Last Checked:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.DATE_LAST_CHECKED
                    ? convertDate(data?.DATE_LAST_CHECKED)
                    : "Undefined"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  Date To Check:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.DATE_TO_CHECK
                    ? convertDate(data?.DATE_TO_CHECK)
                    : "Undefined"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  Service Status:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.SERVICE_STATUS ? data?.SERVICE_STATUS : "Undefined"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container alignItems="center" justifyContent="center" spacing={2}>
          {loading ? (
            <Grid item>
              <CircularProgress size="4rem" />
            </Grid>
          ) : (
            images.length &&
            images.map((image, index) => (
              <Grid item key={index} xs={4}>
                <Card>
                  <CardActionArea>
                    <CardMedia
                      image={image.IMAGE}
                      title={image.IMAGE_NAME || "Tool Image"}
                      sx={{ height: 180 }}
                    />
                  </CardActionArea>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ToolInventoryDetail;
